"use client"

import Link from "next/link"
// next-intl's usePathname, NOT next/navigation's: on www the proxy rewrites
// bare URLs to /en/* internally, so next/navigation reports "/en/..." during
// SSR and the bare-href active-state match fails until a client navigation.
// The i18n variant returns the browser-visible (locale-stripped) path.
import { usePathname } from "@/lib/i18n/routing"
import { useTranslations } from "next-intl"
import {
  LayoutDashboard,
  Bell,
  Fuel,
  Cog,
  Shield,
  Radio,
  Play,
  User,
  LogIn,
} from "lucide-react"
import { ThemeToggleMenu } from "@/components/theme-toggle-menu"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from "@/components/ui/sidebar"

function makeDemoNav(labels: { overview: string; live: string; engines: string; fuel: string; alerts: string }) {
  const base = "/monitoring/demo-dashboard"
  return [
    { name: labels.overview, href: base, icon: LayoutDashboard },
    { name: labels.live, href: `${base}/live`, icon: Radio },
    { name: labels.engines, href: `${base}/engines`, icon: Cog },
    { name: labels.fuel, href: `${base}/fuel`, icon: Fuel },
    { name: labels.alerts, href: `${base}/alerts`, icon: Bell },
  ]
}

function makeOtherNav(label: string) {
  return [
    { name: label, href: "/monitoring/demo-dashboard/try-recording", icon: Play },
  ]
}

export function DemoAppSidebar({ locale }: { locale: string }) {
  const pathname = usePathname()
  const tNav = useTranslations("dashboard.nav")
  const tDemo = useTranslations("demoDashboard.sidebar")
  const demoNav = makeDemoNav({
    overview: tDemo("overview"),
    live: tDemo("live"),
    engines: tNav("engines"),
    fuel: tDemo("fuel"),
    alerts: tNav("alerts"),
  })
  const otherNav = makeOtherNav(tDemo("liveDetection"))
  const overviewHref = "/monitoring/demo-dashboard"

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              render={
                <Link href={overviewHref}>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500 text-black">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div className="grid flex-1 text-left leading-tight">
                    <span className="truncate text-lg font-semibold">GIS Monitoring</span>
                    <span className="truncate text-xs text-amber-400">{tDemo("subtitle")}</span>
                  </div>
                </Link>
              }
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{tDemo("operations")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {demoNav.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    isActive={
                      item.href === overviewHref
                        ? pathname === overviewHref
                        : pathname.startsWith(item.href)
                    }
                    tooltip={item.name}
                    render={
                      <Link href={item.href}>
                        <item.icon />
                        <span>{item.name}</span>
                      </Link>
                    }
                  />
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupLabel>{tDemo("tryIt")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {otherNav.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    tooltip={item.name}
                    render={
                      <Link href={item.href}>
                        <item.icon />
                        <span>{item.name}</span>
                      </Link>
                    }
                  />
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              tooltip={tDemo("signupCta")}
              className="bg-orange-500 text-white hover:bg-orange-600 hover:text-white data-[active=true]:bg-orange-500"
              render={
                <Link href="/sign-up">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white/15">
                    <LogIn className="h-4 w-4" />
                  </div>
                  <div className="grid flex-1 text-left leading-tight">
                    <span className="truncate text-sm font-semibold">{tDemo("signupCta")}</span>
                    <span className="truncate text-xs opacity-90">{tDemo("signupCtaSub")}</span>
                  </div>
                </Link>
              }
            />
          </SidebarMenuItem>
        </SidebarMenu>
        <ThemeToggleMenu />
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" className="cursor-default">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-sm font-medium">{tDemo("demoVisitor")}</span>
                <span className="truncate text-xs text-muted-foreground">{tDemo("sampleData")}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
